/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useContext, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import { RootState } from '../../../store';
import { flightLegMapWrapper } from '../../../store/flightLegs/actions';
import { flightLegServiceAlertActionWrapper } from '../../../store/flightLegServiceAlerts/actions';
import { mapActionWrapper } from '../../../store/maps/actions';
import FullScreenMapControl from '../map_controls/FullScreenMapControl';
import OverlaysMapControl from '../map_controls/OverlaysMapControl';
import PerformanceOutlookLegendPanelControl from '../map_controls/PerformanceOutlookLegendPanelControl';
import SettingsMapControl from '../map_controls/SettingsMapControl';
import TailTagControl from '../map_controls/TailTagControl';
import ZoomMapControl from '../map_controls/ZoomMapControl';
import BaseMapComponent, { MapContext } from './BaseMapComponent';
import BaseMapLoadingContainer from './BaseMapLoadingContainer';
import { GOOGLE_MAP_INSTANCE_KEY } from './GoogleMapSingleton';
import MapZoomListener from './MapZoomListener';
import RulerToolMapControl from '../map_controls/RulerToolMapControl';
import LatLngMapDisplay from './LatLngMapDisplay';

export const FullscreenContext = React.createContext(false);

export const MapActionsContext = React.createContext({
  mapActions: mapActionWrapper(GOOGLE_MAP_INSTANCE_KEY),
  flightLegsActions: flightLegMapWrapper(GOOGLE_MAP_INSTANCE_KEY),
  serviceAlertsActions: flightLegServiceAlertActionWrapper(GOOGLE_MAP_INSTANCE_KEY),
});
export const MapIdContext = React.createContext(GOOGLE_MAP_INSTANCE_KEY);

/**
 * Custom hook that abstracts the map id away from the resulting selector function
 */
export function useSelectorWithMapId<T>(
  selector: (mapId: string) => (state: RootState) => T,
  equalityFunc = shallowEqual,
): T {
  const mapId = useContext(MapIdContext);
  return useSelector(selector(mapId), equalityFunc);
}

export interface BaseMapContainerProps {
  children: React.ReactNode;
  width?: string;
  left?: string;
  height?: string;
  mapLabelsEnabled: boolean;
  mapSatelliteViewEnabled: boolean;
  mapPerformanceOutlookLegendPanelEnabled: boolean;
  top?: string;
  additionalMapControls?: (map: google.maps.Map, isFullscreen: boolean) => React.ReactNode;
  persistZoom?: boolean;
  onlyShowZoomControls?: boolean;

  // MUST BE UNIQUE
  id: string;
}

/**
 * The BaseMapContainer is the container of the google maps api. It renders a map given once
 * the google map API is loaded in the browser. Also wraps the common map controls that
 * are used throughout the UP.
 */
const BaseMapContainer = ({
  children,
  width,
  left,
  height,
  mapLabelsEnabled,
  mapSatelliteViewEnabled,
  mapPerformanceOutlookLegendPanelEnabled,
  top,
  additionalMapControls,
  id,
  persistZoom,
  onlyShowZoomControls,
}: BaseMapContainerProps) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const contextActions = useMemo(
    () => ({
      mapActions: mapActionWrapper(id),
      flightLegsActions: flightLegMapWrapper(id),
      serviceAlertsActions: flightLegServiceAlertActionWrapper(id),
    }),
    [id],
  );
  return (
    <FullscreenContext.Provider value={isFullscreen}>
      <BaseMapLoadingContainer>
        <MapActionsContext.Provider value={contextActions}>
          <MapIdContext.Provider value={id}>
            <BaseMapComponent
              width={isFullscreen ? '100%' : width}
              left={isFullscreen ? '0px' : left}
              height={isFullscreen ? '100%' : height}
              mapLabelsEnabled={mapLabelsEnabled}
              mapSatelliteViewEnabled={mapSatelliteViewEnabled}
              top={top}
              id={id}
            >
              <MapContext.Consumer>
                {map => (
                  <React.Fragment>
                    {!onlyShowZoomControls && <LatLngMapDisplay map={map} />}
                    {mapPerformanceOutlookLegendPanelEnabled && !onlyShowZoomControls && (
                      <PerformanceOutlookLegendPanelControl map={map} isFullScreen={isFullscreen} />
                    )}
                    {!onlyShowZoomControls && <TailTagControl map={map} isFullScreen={isFullscreen} />}
                    {!onlyShowZoomControls && <OverlaysMapControl map={map} isFullScreen={isFullscreen} />}
                    {!onlyShowZoomControls && <SettingsMapControl map={map} isFullScreen={isFullscreen} />}
                    {!onlyShowZoomControls && <RulerToolMapControl map={map} isFullScreen={isFullscreen} />}
                    <ZoomMapControl map={map} addMarginTop={onlyShowZoomControls} />
                    {!onlyShowZoomControls && (
                      <FullScreenMapControl
                        map={map}
                        mapId={id}
                        onEnterFullScreen={() => {
                          setIsFullscreen(true);
                        }}
                        onExitFullScreen={() => {
                          setIsFullscreen(false);
                        }}
                      />
                    )}
                    {additionalMapControls && !onlyShowZoomControls && additionalMapControls(map, isFullscreen)}
                    <MapZoomListener map={map} mapId={id} persistZoom={persistZoom} />
                  </React.Fragment>
                )}
              </MapContext.Consumer>
              {children}
            </BaseMapComponent>
          </MapIdContext.Provider>
        </MapActionsContext.Provider>
      </BaseMapLoadingContainer>
    </FullscreenContext.Provider>
  );
};

export default withTheme(BaseMapContainer);
